import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import QuizTimer from './QuizTimer';

/**
 * Quiz Question Component
 * Displays a single quiz question with answer options and timer
 */
const QuizQuestion = ({
  question,
  options,
  selectedAnswer,
  isSubmitted,
  correctAnswer,
  timeLeft,
  totalTime,
  onSelectAnswer,
  onSubmitAnswer,
  onTimeUp,
  questionNumber,
  totalQuestions,
  category = '',
  difficulty = ''
}) => {
  // Get difficulty color
  const getDifficultyColor = () => {
    switch (difficulty.toLowerCase()) {
      case 'hard':
        return 'text-red-500';
      case 'medium':
        return 'text-amber-500';
      case 'easy':
        return 'text-green-500';
      default:
        return 'text-blue-500';
    }
  };
  
  // Determine if an option is correct or incorrect after submission
  const getOptionClass = (option) => {
    if (!isSubmitted) {
      return selectedAnswer === option
        ? 'border-green-500 bg-green-50 ring-2 ring-green-300' 
        : 'border-gray-200 hover:border-green-200 hover:bg-green-50';
    }
    
    if (option === correctAnswer) {
      return 'border-green-500 bg-green-100 ring-1 ring-green-300';
    }
    
    if (selectedAnswer === option && option !== correctAnswer) {
      return 'border-red-500 bg-red-100 ring-1 ring-red-300';
    }
    
    return 'border-gray-200 opacity-60';
  };
  
  // Get option letter (A, B, C, D)
  const getOptionLetter = (index) => {
    return String.fromCharCode(65 + index);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-6 bg-white rounded-xl relative"
    >
      {/* Question Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="max-w-[75%]">
          {category && (
            <div className="text-xs text-gray-500 mb-1 flex items-center">
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {category}
              </span>
              {difficulty && (
                <span className={`ml-2 ${getDifficultyColor()}`}>
                  {difficulty} difficulty
                </span>
              )}
            </div>
          )}
          
          <div className="text-sm font-medium text-gray-500 mb-1">
            Question {questionNumber} of {totalQuestions}
          </div>
          
          <h2 className="text-xl font-bold text-gray-800">{question}</h2>
        </div>
        
        <QuizTimer 
          timeLeft={timeLeft} 
          totalTime={totalTime} 
          onTimeUp={onTimeUp} 
        />
      </div>
      
      {/* Answer Options */}
      <div className="space-y-3">
        <AnimatePresence>
          {options.map((option, index) => (
            <motion.button
              key={option}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => !isSubmitted && onSelectAnswer(option)}
              disabled={isSubmitted}
              className={`w-full p-4 rounded-lg border text-left transition-all relative group ${getOptionClass(option)}`}
            >
              <div className="flex items-center">
                <div className={`w-7 h-7 flex items-center justify-center rounded-full border mr-3 ${
                  selectedAnswer === option 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : 'border-gray-300 text-gray-500 group-hover:border-green-300'
                }`}>
                  {getOptionLetter(index)}
                </div>
                <span className="text-gray-800 font-medium">{option}</span>
              </div>
              
              {/* Feedback indicators shown after submission */}
              {isSubmitted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2"
                >
                  {option === correctAnswer ? (
                    <CheckCircle size={22} className="text-green-500" />
                  ) : selectedAnswer === option ? (
                    <XCircle size={22} className="text-red-500" />
                  ) : null}
                </motion.div>
              )}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
      
      {/* Submit/Next Button */}
      {!isSubmitted ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6"
        >
          <button
            onClick={onSubmitAnswer}
            disabled={!selectedAnswer}
            className="w-full py-3 px-6 bg-gradient-to-r from-green-500 to-teal-500 text-white font-medium rounded-lg shadow-sm hover:from-green-600 hover:to-teal-600 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
          >
            Submit Answer
          </button>
          
          {!selectedAnswer && (
            <div className="text-sm text-center mt-2 text-gray-500">
              Select an answer to continue
            </div>
          )}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          {/* Feedback Message */}
          <div className="mb-4">
            {selectedAnswer === correctAnswer ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="text-green-500 mr-2 mt-0.5" size={18} />
                  <div>
                    <p className="font-medium text-green-800">
                      Correct! +10 points
                    </p>
                    <p className="text-sm mt-0.5 text-green-700">
                      Great job! Let's continue to the next question.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="text-red-500 mr-2 mt-0.5" size={18} />
                  <div>
                    <p className="font-medium text-red-800">
                      {selectedAnswer ? 'Incorrect' : 'Time\'s up!'}
                    </p>
                    <p className="text-sm mt-0.5 text-red-700">
                      The correct answer is: <span className="font-semibold">{correctAnswer}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={onTimeUp}
            className="w-full py-3 px-6 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-medium rounded-lg shadow-sm hover:from-gray-700 hover:to-gray-800 transition-all"
          >
            Next Question
          </button>
        </motion.div>
      )}
      
      {/* Question Number Indicator (subtle design) */}
      <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 text-[10px] font-bold text-gray-400">
        {questionNumber} / {totalQuestions}
      </div>
    </motion.div>
  );
};

export default QuizQuestion;